import type {
  HoldCleaningInformationFilter,
  HoldCleaningAssessmentPayload,
  HoldCleaningAssessmentResult,
  HoldCleaningCrewSummary,
  HoldCleaningEmailIntelligenceStatus,
  HoldCleaningImage,
  HoldCleaningRefreshRequest,
  HoldCleaningRefreshRun,
  HoldCleaningRefreshStatus,
  HoldCleaningRunPage,
  HoldCleaningSourceHealth,
  HoldCleaningVoyageDetail,
  HoldCleaningVoyagePage,
  HoldCleaningVoyageQuery,
  HoldCleaningVoyageSummary,
} from "../models/hold-cleaning.models";

const now = "2026-08-14T08:35:00.000Z";
const datasetId = "mock-dataset-2026-08-14-0500";

const voyages: HoldCleaningVoyageSummary[] = [
  {
    voyageId: "mock-voyage-alpine-2412",
    sourceVoyageKey: "ALP2412",
    imo: "9725613",
    vesselName: "Alpine Star",
    vesselCode: "ALP",
    voyageNumber: "2412",
    status: "IN_PROGRESS",
    startAt: "2026-08-10T06:00:00.000Z",
    loadPort: "Santos",
    dischargePort: "Rotterdam",
    cargo: "Soybean meal",
    previousCargo: "Coal",
    nextCargo: "Wheat",
    nextLoadPort: "New Orleans",
    nextEta: "2026-08-28T06:00:00.000Z",
    laycanStartAt: "2026-08-29T00:00:00.000Z",
    laycanEndAt: "2026-08-31T23:59:00.000Z",
    cleaningStatus: {
      code: "ACTION_REQUIRED",
      label: "Action required",
      detail: "Staining remains in hold 5.",
      implementationStatus: "AVAILABLE",
    },
    latestRating: { score: null, maximumScore: 3, label: "—", implementationStatus: "NOT_IMPLEMENTED" },
    numberOfHolds: 7,
    scopeReason: "Cargo transition requires full hold-cleaning assessment.",
    sourceUpdatedAt: now,
    formCount: 2,
    imageCount: 4,
    activeDatasetId: datasetId,
  },
  {
    voyageId: "mock-voyage-baltic-1187",
    sourceVoyageKey: "BAL1187",
    imo: "9687445",
    vesselName: "Baltic Horizon",
    vesselCode: "BAL",
    voyageNumber: "1187",
    status: "PLANNING",
    startAt: "2026-08-18T12:00:00.000Z",
    loadPort: "New Orleans",
    dischargePort: "Antwerp",
    cargo: "Wheat",
    previousCargo: "Petcoke",
    numberOfHolds: 5,
    scopeReason: "High-risk previous cargo.",
    sourceUpdatedAt: "2026-08-14T07:55:00.000Z",
    formCount: 1,
    imageCount: 0,
    activeDatasetId: datasetId,
  },
  {
    voyageId: "mock-voyage-coral-0904",
    sourceVoyageKey: "COR0904",
    imo: "9791038",
    vesselName: "Coral Bay",
    vesselCode: "COR",
    voyageNumber: "0904",
    status: "SCHEDULED",
    startAt: "2026-08-21T09:30:00.000Z",
    loadPort: "Kwinana",
    dischargePort: "Kaohsiung",
    cargo: "Barley",
    previousCargo: "Iron ore",
    numberOfHolds: 7,
    scopeReason: "Upcoming voyage within planning window.",
    sourceUpdatedAt: "2026-08-14T07:40:00.000Z",
    formCount: 0,
    imageCount: 0,
    activeDatasetId: datasetId,
  },
  {
    voyageId: "mock-voyage-delta-3201",
    sourceVoyageKey: "DEL3201",
    imo: "9618224",
    vesselName: "Delta Pioneer",
    vesselCode: "DEL",
    voyageNumber: "3201",
    status: "COMPLETED",
    startAt: "2026-07-19T10:00:00.000Z",
    completedAt: "2026-08-02T16:15:00.000Z",
    loadPort: "Paranagua",
    dischargePort: "Hamburg",
    cargo: "Corn",
    previousCargo: "Bauxite",
    numberOfHolds: 7,
    scopeReason: "Historical comparison voyage.",
    sourceUpdatedAt: "2026-08-03T05:05:00.000Z",
    formCount: 2,
    imageCount: 6,
    activeDatasetId: datasetId,
  },
];

const dataset = {
  datasetId,
  status: "ACTIVE" as const,
  activatedAt: now,
  collectedAt: "2026-08-14T08:31:00.000Z",
  isPartial: true,
  warnings: [],
};

export const mockListHoldCleaningVoyages = (
  query: HoldCleaningVoyageQuery,
  vesselsOnly = false,
): HoldCleaningVoyagePage => {
  const term = query.search?.trim().toLowerCase();
  const mockDataCoverage: Record<string, NonNullable<HoldCleaningVoyageQuery["dataCoverage"]>> = {
    "mock-voyage-alpine-2412": "FULL",
    "mock-voyage-baltic-1187": "PARTIAL",
    "mock-voyage-coral-0904": "NONE",
    "mock-voyage-delta-3201": "PARTIAL",
  };
  const available = voyages.filter((voyage) => {
    if (
      vesselsOnly &&
      (["SCHEDULED", "PLANNED", "PLANNING", "FIXED", "CANCELLED"].includes(voyage.status) ||
        mockDataCoverage[voyage.voyageId] === "NONE")
    )
      return false;
    if (query.imo && voyage.imo !== query.imo) return false;
    if (query.status && voyage.status !== query.status) return false;
    if (query.dataCoverage && mockDataCoverage[voyage.voyageId] !== query.dataCoverage) return false;
    if (!term) return true;
    return [voyage.vesselName, voyage.imo, voyage.voyageNumber, voyage.cargo]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term));
  });
  const phaseOf = (status: string): "planning" | "transit" | "other" => {
    const normalized = status.trim().toUpperCase().replaceAll("-", "_");
    if (["SCHEDULED", "PLANNING", "PLANNED", "FIXED"].some((value) => normalized.includes(value))) return "planning";
    if (["COMMENCED", "ACTIVE", "IN_PROGRESS", "IN PROGRESS"].some((value) => normalized.includes(value)))
      return "transit";
    return "other";
  };
  const hasInformation = (voyage: HoldCleaningVoyageSummary, information: HoldCleaningInformationFilter): boolean => {
    const detail = mockGetHoldCleaningVoyage(voyage.voyageId);
    switch (information) {
      case "PHOTOS":
        return (voyage.imageCount ?? 0) > 0;
      case "PHOTOS_HISTORY":
        return voyages
          .filter(
            (previous) =>
              previous.imo === voyage.imo &&
              !["SCHEDULED", "PLANNED", "PLANNING", "FIXED", "CANCELLED"].includes(previous.status) &&
              Number(previous.voyageNumber) < Number(voyage.voyageNumber),
          )
          .sort((left, right) => Number(right.voyageNumber) - Number(left.voyageNumber))
          .slice(0, 4)
          .some((previous) => (previous.imageCount ?? 0) > 0);
      case "CHEMICALS_EQUIPMENT":
        return Boolean(detail.currentCleaning?.inventory?.length);
      case "CLEANING_PROGRESS":
        return Boolean(detail.currentCleaning?.taskStatuses.some((step) => step.status !== "NOT_REPORTED"));
      case "CREW_REPORT":
        return Boolean(detail.crew?.totalCrew);
    }
  };
  const count = (information: HoldCleaningInformationFilter) =>
    available.filter(
      (voyage) => (!query.phase || phaseOf(voyage.status) === query.phase) && hasInformation(voyage, information),
    ).length;
  const informationCounts = {
    PHOTOS: count("PHOTOS"),
    PHOTOS_HISTORY: count("PHOTOS_HISTORY"),
    CHEMICALS_EQUIPMENT: count("CHEMICALS_EQUIPMENT"),
    CLEANING_PROGRESS: count("CLEANING_PROGRESS"),
    CREW_REPORT: count("CREW_REPORT"),
  };
  const matching = available.filter((voyage) =>
    (query.information ?? []).every((value) => hasInformation(voyage, value)),
  );
  const phaseCounts = {
    all: matching.length,
    planning: matching.filter((voyage) => phaseOf(voyage.status) === "planning").length,
    transit: matching.filter((voyage) => phaseOf(voyage.status) === "transit").length,
  };
  const filtered = query.phase ? matching.filter((voyage) => phaseOf(voyage.status) === query.phase) : matching;
  const sortValues = (voyage: HoldCleaningVoyageSummary): string | null => {
    switch (query.sortBy) {
      case "vessel":
        return voyage.vesselName;
      case "cargo":
        return voyage.cargo ?? voyage.previousCargo ?? null;
      case "dischargePort":
        return voyage.dischargePort ?? null;
      case "dischargeCompletion":
        return voyage.completedAt ?? null;
      case "nextCargo":
        return voyage.nextCargo ?? null;
      case "loadPort":
        return voyage.nextLoadPort ?? null;
      case "eta":
        return voyage.nextEta ?? null;
      case "laycan":
        return voyage.laycanStartAt ?? null;
      default:
        return null;
    }
  };
  if (query.sortBy)
    filtered.sort((left, right) => {
      const a = sortValues(left);
      const b = sortValues(right);
      if (a === null && b !== null) return 1;
      if (b === null && a !== null) return -1;
      const comparison = (a ?? "").localeCompare(b ?? "", undefined, { numeric: true });
      return (query.sortDirection === "desc" ? -comparison : comparison) || left.voyageId.localeCompare(right.voyageId);
    });
  const offset = query.sortBy
    ? (query.page - 1) * query.pageSize
    : query.cursor
      ? Number(query.cursor)
      : (query.page - 1) * query.pageSize;
  const start = Number.isInteger(offset) && offset >= 0 ? offset : 0;
  return {
    items: filtered.slice(start, start + query.pageSize),
    phaseCounts,
    informationCounts: vesselsOnly ? informationCounts : undefined,
    page: query.page,
    pageSize: query.pageSize,
    total: filtered.length,
    nextCursor: start + query.pageSize < filtered.length ? String(start + query.pageSize) : null,
    dataset,
  };
};

const crew: HoldCleaningCrewSummary = {
  totalCrew: 21,
  officers: 9,
  ratings: 12,
  rankCounts: [
    { designationRank: "Master", count: 1 },
    { designationRank: "Deck officers", count: 4 },
    { designationRank: "Engine officers", count: 4 },
    { designationRank: "Ratings", count: 12 },
  ],
  sourceUpdatedAt: "2026-08-13T19:10:00.000Z",
  warnings: [],
};

export const mockGetHoldCleaningVoyage = (voyageId: string): HoldCleaningVoyageDetail => {
  const voyage = voyages.find((item) => item.voyageId === voyageId || item.imo === voyageId) ?? voyages[0];
  return {
    voyage,
    dataset,
    crew,
    forms: [
      {
        formId: `${voyage.voyageId}-form`,
        templateName: "Hold Cleaning Preparation",
        status: "SUBMITTED",
        submittedAt: "2026-08-13T14:20:00.000Z",
        sections: [
          {
            title: "Cleaning inventory",
            fields: [
              { label: "Fresh water", value: 148, unit: "mt" },
              { label: "Chemicals available", value: 920, unit: "l" },
              { label: "High pressure machines", value: 3, unit: "units" },
              { label: "Cleaning gangs", value: 2, unit: "teams" },
            ],
          },
          {
            title: "Readiness checks",
            fields: [
              { label: "Bilge protection installed", value: true },
              { label: "Residue disposal arranged", value: true },
              { label: "Ventilation test complete", value: false },
            ],
          },
        ],
      },
    ],
    lineage: [
      { source: "snowflake", sourceRecordId: "IMOS_DATASHARE.VOYAGES/ALP2412", version: "mock-v1", capturedAt: now },
      {
        source: "shipfix",
        sourceRecordId: "SHIPFIX-HC-44712",
        version: "mock-v1",
        capturedAt: "2026-08-14T08:20:00.000Z",
      },
      {
        source: "crew-report",
        sourceRecordId: "opslink.crew_report/9725613",
        version: null,
        capturedAt: crew.sourceUpdatedAt,
      },
    ],
    warnings: dataset.warnings,
    vesselFacts: {
      builtAt: "2018-01-01T00:00:00.000Z",
      registeredOwner: "Alpine Maritime Ltd",
      shipManager: "Example Ship Management",
      deadweightTonnes: 81500,
      shipType: "Kamsarmax bulk carrier",
      numberOfHatches: 7,
      grainCapacityCubicFeet: 3400000,
      gearDescription: "Gearless",
      implementationStatus: "AVAILABLE",
      source: "SNOWFLAKE",
    },
    currentCleaning: {
      reportName: "daily progress",
      reportUpdatedAt: "2026-08-14T06:30:00.000Z",
      completedSteps: 8,
      totalSteps: 13,
      outstandingItem: "Rewash staining in hold 5",
      workLogDays: 3,
      taskStatuses: Array.from({ length: 7 }, (_, holdIndex) =>
        Array.from({ length: 13 }, (_, stepIndex) => ({
          holdNumber: holdIndex + 1,
          stepNumber: stepIndex + 1,
          status: (stepIndex < 8 ? "COMPLETED" : stepIndex === 8 ? "IN_PROGRESS" : "PLANNING") as
            | "COMPLETED"
            | "IN_PROGRESS"
            | "PLANNING",
          source: holdIndex === 4 && stepIndex === 8 ? ("MANUAL" as const) : ("EMAIL_INTELLIGENCE" as const),
        })),
      ).flat(),
      workLog: [
        {
          occurredAt: "2026-08-13T18:00:00.000Z",
          description: "Fresh-water rinse completed in holds 1-4.",
          holdNumbers: [1, 2, 3, 4],
          source: "EMAIL_INTELLIGENCE",
        },
        {
          occurredAt: "2026-08-14T06:00:00.000Z",
          description: "Hold 5 aft bulkhead scheduled for repeat wash.",
          holdNumbers: [5],
          source: "MANUAL",
        },
      ],
      holdProgress: Array.from({ length: 7 }, (_, index) => ({
        holdNumber: index + 1,
        percentage: index === 4 ? 62 : 72,
        stage: index === 4 ? "Repeat wash" : "Drying and inspection",
        source: index === 4 ? ("MANUAL" as const) : ("EMAIL_INTELLIGENCE" as const),
      })),
      outstandingItems: [
        { description: "Rewash staining in hold 5", holdNumbers: [5], stepNumber: 9, state: "OPEN", source: "MANUAL" },
      ],
      inventory: [
        { label: "Fresh water", value: "148", unit: "mt", source: "FORM" },
        { label: "Cleaning chemicals", value: "920", unit: "l", source: "EMAIL_INTELLIGENCE" },
        { label: "High pressure machines", value: "3", unit: "units", source: "MANUAL" },
      ],
      fieldSources: { report: "EMAIL_INTELLIGENCE" },
      implementationStatus: "AVAILABLE",
    },
    history: {
      cleaningWindow: "4 days",
      manHours: 428,
      standardRequired: "Grain clean",
      cleaningMethod: "Dry sweep, seawater wash, fresh-water rinse and forced ventilation",
      failedHolds: [
        {
          holdNumber: 5,
          stage: "Inspection",
          reason: "Aft bulkhead staining",
          resolution: null,
          state: "OPEN",
          source: "MANUAL",
        },
      ],
      maintenanceEvents: [
        {
          title: "Touch-up painting",
          detail: "Lower hopper plates in hold 3.",
          occurredAt: "2026-08-12T08:00:00.000Z",
          source: "EMAIL_INTELLIGENCE",
        },
      ],
      warnings: [
        {
          description: "Chemical ROB should be confirmed before the final wash.",
          state: "OPEN",
          source: "EMAIL_INTELLIGENCE",
        },
      ],
      linkedDocuments: [],
      notes: [
        {
          noteId: "mock-note-1",
          text: "Chief Officer confirmed additional freshwater is available.",
          author: "ops.support@cargill.com",
          createdAt: "2026-08-14T07:15:00.000Z",
        },
      ],
      holdRatings: [],
      fieldSources: {
        cleaningWindow: "EMAIL_INTELLIGENCE",
        manHours: "MANUAL",
        standardRequired: "EMAIL_INTELLIGENCE",
        cleaningMethod: "EMAIL_INTELLIGENCE",
      },
      implementationStatus: "AVAILABLE",
    },
  };
};

export const mockGetHoldCleaningCrew = (): HoldCleaningCrewSummary => crew;

const images: HoldCleaningImage[] = [1, 2, 5, 7].map((holdNumber) => ({
  imageId: `mock-hold-${holdNumber}`,
  holdNumber,
  mimeType: "image/svg+xml",
  capturedAt: `2026-08-13T1${holdNumber}:10:00.000Z`,
  source: holdNumber % 2 ? "shipfix" : "outlook",
  caption: holdNumber === 5 ? "Staining remains near the aft bulkhead." : "Post-sweep evidence submitted by vessel.",
}));

export const mockGetHoldCleaningImages = (): HoldCleaningImage[] => images;

export const mockGetHoldCleaningImageContent = (imageId: string): Blob => {
  const hold = imageId.replace("mock-hold-", "");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 360"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#163f3b"/><stop offset="1" stop-color="#80aaa1"/></linearGradient></defs><rect width="640" height="360" fill="url(#g)"/><path d="M70 280 150 105h340l80 175M150 105l70 175m270-175-70 175" fill="none" stroke="#dff2f0" stroke-width="10" opacity=".65"/><text x="40" y="55" fill="white" font-family="Arial" font-size="24">Modeled hold evidence</text><text x="40" y="330" fill="white" font-family="Arial" font-size="42" font-weight="700">Hold ${hold}</text></svg>`;
  return new Blob([svg], { type: "image/svg+xml" });
};

const initialAssessment: HoldCleaningAssessmentResult = {
  etag: 'W/"mock-assessment:3"',
  assessment: {
    assessmentId: "mock-assessment-01",
    voyageId: voyages[0].voyageId,
    revision: 3,
    payload: {
      readiness: "READY_WITH_ACTIONS",
      summary: "Cleaning can finish before arrival if hold 5 staining and ventilation checks are closed.",
      actions: [
        { action: "Rewash the aft bulkhead in hold 5", owner: "Chief Officer", dueAt: "2026-08-16T18:00:00.000Z" },
        { action: "Upload ventilation test evidence", owner: "Second Officer", dueAt: "2026-08-17T06:00:00.000Z" },
      ],
      evidence: [{ source: "Shipfix", referenceId: "mock-hold-5", note: "Residual staining visible." }],
      imageIds: images.map((image) => image.imageId),
    },
    createdBy: "mock.hold-cleaning@cargill.com",
    createdAt: "2026-08-14T08:30:00.000Z",
    source: "HUMAN",
    modelId: "mock-preview",
    promptContractVersion: "hold-cleaning-assessment-v1",
  },
};

const savedAssessments = new Map<string, HoldCleaningAssessmentResult>([
  [initialAssessment.assessment.voyageId, initialAssessment],
]);

export const mockGetHoldCleaningAssessment = (voyageId: string): HoldCleaningAssessmentResult =>
  savedAssessments.get(voyageId) ?? {
    etag: "*",
    assessment: {
      assessmentId: "",
      voyageId,
      revision: 0,
      payload: { readiness: "UNASSESSED", summary: "", actions: [], evidence: [], imageIds: [] },
      createdBy: "",
      createdAt: "",
      source: "HUMAN",
    },
  };

export const mockGenerateHoldCleaningAssessment = (
  voyageId: string,
  imageIds: string[],
): HoldCleaningAssessmentResult => {
  const current = mockGetHoldCleaningAssessment(voyageId);
  return {
    etag: current.etag,
    assessment: {
      ...current.assessment,
      source: "AI",
      payload: {
        ...current.assessment.payload,
        summary:
          current.assessment.payload.summary ||
          "AI draft: review hold condition and cleaning progress before deciding readiness.",
        imageIds,
      },
    },
  };
};

export const mockSaveHoldCleaningAssessment = (
  voyageId: string,
  payload: HoldCleaningAssessmentPayload,
): HoldCleaningAssessmentResult => {
  const current = mockGetHoldCleaningAssessment(voyageId);
  const revision = current.assessment.revision + 1;
  const saved: HoldCleaningAssessmentResult = {
    etag: `W/"00000000-0000-4000-8000-000000000001:${revision}"`,
    assessment: {
      ...current.assessment,
      assessmentId: current.assessment.assessmentId || "00000000-0000-4000-8000-000000000001",
      voyageId,
      revision,
      payload,
      source: "HUMAN",
      createdAt: new Date().toISOString(),
    },
  };
  savedAssessments.set(voyageId, saved);
  return saved;
};

export const mockSourceHealth: HoldCleaningSourceHealth[] = [
  {
    source: "snowflake",
    state: "healthy",
    lastSuccessfulAt: now,
    lastAttemptedAt: now,
    checkpointAt: "2026-08-14T08:28:00.000Z",
    recordsProcessed: 184,
  },
  {
    source: "shipfix",
    state: "healthy",
    lastSuccessfulAt: "2026-08-14T08:33:00.000Z",
    lastAttemptedAt: now,
    checkpointAt: "2026-08-14T08:30:00.000Z",
    recordsProcessed: 62,
  },
  {
    source: "outlook",
    state: "healthy",
    lastSuccessfulAt: "2026-08-14T08:32:00.000Z",
    lastAttemptedAt: now,
    checkpointAt: "2026-08-14T08:25:00.000Z",
    recordsProcessed: 29,
  },
  {
    source: "email-intelligence",
    state: "warning",
    lastSuccessfulAt: "2026-08-14T08:34:00.000Z",
    lastAttemptedAt: now,
    checkpointAt: "2026-08-14T08:34:00.000Z",
    recordsProcessed: 159,
    warning: "25 historical emails remain.",
  },
];

const successRun: HoldCleaningRefreshRun = {
  runId: "mock-run-success-0814",
  requestId: "mock-request-success-0814",
  datasetId,
  status: "SUCCEEDED",
  trigger: "SCHEDULED",
  requestedAt: "2026-08-14T03:00:00.000Z",
  requestedBy: "captain-schedule",
  startedAt: "2026-08-14T03:00:18.000Z",
  completedAt: "2026-08-14T03:11:42.000Z",
  currentStage: "complete",
  warningCount: 2,
  sourceStatuses: mockSourceHealth,
  validationSummary: { voyages: 184, inScope: 31, images: 96, activated: true },
};

const failedRun: HoldCleaningRefreshRun = {
  ...successRun,
  runId: "mock-run-failed-0813",
  requestId: "mock-request-failed-0813",
  datasetId: null,
  status: "FAILED_RETRYABLE",
  trigger: "MANUAL",
  requestedAt: "2026-08-13T14:03:00.000Z",
  requestedBy: "admin.preview@cargill.com",
  startedAt: "2026-08-13T14:15:04.000Z",
  completedAt: "2026-08-13T14:22:31.000Z",
  currentStage: "collect:outlook",
  warningCount: 1,
  errorCode: "SOURCE_TIMEOUT",
  errorSummary: "Outlook collection timed out after the configured retry window.",
  sourceStatuses: mockSourceHealth.map((source) =>
    source.source === "outlook" ? { ...source, state: "failed", warning: "SOURCE_TIMEOUT" } : source,
  ),
  validationSummary: { activated: false, completedSources: 3, failedSource: "outlook" },
};

let mockRequest: HoldCleaningRefreshRequest | null = null;
let mockRun: HoldCleaningRefreshRun | null = null;

const launchMockRun = (trigger: "MANUAL" | "RETRY"): HoldCleaningRefreshRequest => {
  const stamp = Date.now();
  const requestId = `mock-request-${stamp}`;
  mockRequest = { requestId, runId: null, status: "REQUESTED", requestedAt: new Date().toISOString() };
  mockRun = {
    ...successRun,
    runId: `mock-run-${stamp}`,
    requestId,
    datasetId: null,
    status: "RUNNING",
    trigger,
    requestedAt: mockRequest.requestedAt,
    requestedBy: "frontend-mock@cargill.com",
    startedAt: new Date(stamp + 1_000).toISOString(),
    completedAt: null,
    currentStage: "collect:shipfix",
    warningCount: 0,
    sourceStatuses: mockSourceHealth.map((source) => ({
      ...source,
      state: source.source === "snowflake" ? "healthy" : "unknown",
    })),
    validationSummary: { mock: true, message: "This run is simulated entirely in the browser." },
  };
  return mockRequest;
};

export const mockRequestHoldCleaningRefresh = () => launchMockRun("MANUAL");
export const mockRetryHoldCleaningRefreshRun = () => launchMockRun("RETRY");

export const mockGetHoldCleaningRefreshRequest = (requestId: string): HoldCleaningRefreshRequest => {
  if (!mockRequest || mockRequest.requestId !== requestId)
    return { requestId, runId: failedRun.runId, status: "CLAIMED", requestedAt: failedRun.requestedAt };
  if (Date.now() - new Date(mockRequest.requestedAt).getTime() > 3_000 && mockRun) {
    mockRequest = { ...mockRequest, runId: mockRun.runId, status: "CLAIMED" };
  }
  return mockRequest;
};

export const mockGetHoldCleaningRefreshRun = (runId: string): HoldCleaningRefreshRun => {
  if (mockRun?.runId === runId) {
    const elapsed = Date.now() - new Date(mockRun.requestedAt).getTime();
    if (elapsed > 22_000)
      mockRun = {
        ...mockRun,
        status: "SUCCEEDED",
        datasetId,
        currentStage: "complete",
        completedAt: new Date().toISOString(),
        validationSummary: successRun.validationSummary,
      };
    else if (elapsed > 12_000) mockRun = { ...mockRun, status: "VALIDATING", currentStage: "validate:dataset" };
    return mockRun;
  }
  return runId === failedRun.runId ? failedRun : successRun;
};

export const mockListHoldCleaningRefreshRuns = (page: number, pageSize: number): HoldCleaningRunPage => {
  const items = [...(mockRun ? [mockRun] : []), successRun, failedRun].slice(0, pageSize);
  return { items, page, pageSize, total: items.length, nextCursor: null };
};

export const mockGetHoldCleaningRefreshStatus = (): HoldCleaningRefreshStatus => ({
  pendingCount: mockRequest && !mockRequest.runId ? 1 : 0,
  activeRun:
    mockRun && !["SUCCEEDED", "FAILED_RETRYABLE", "FAILED_FINAL", "STALE", "CANCELLED"].includes(mockRun.status)
      ? {
          requestId: mockRun.requestId,
          runId: mockRun.runId,
          status: mockRun.status,
          requestedAt: mockRun.requestedAt,
          requestedBy: mockRun.requestedBy,
        }
      : null,
  latestSuccess: successRun,
  latestFailure: failedRun,
  schedule: {
    expression: "0 5 * * *",
    localTime: "05:00",
    timeZone: "Europe/Zurich",
  },
});

export const mockGetHoldCleaningEmailIntelligenceStatus = (): HoldCleaningEmailIntelligenceStatus => ({
  baselineAt: "2026-08-13T05:00:00.000Z",
  discoveredCount: 184,
  attemptedCount: 164,
  processedCount: 159,
  relevantCount: 91,
  irrelevantCount: 68,
  extractedCount: 86,
  retryableFailureCount: 5,
  finalFailureCount: 0,
  remainingCount: 25,
  promptTokens: 294000,
  completionTokens: 41000,
  totalTokens: 335000,
  estimatedCostUsd: 0.11005,
  pricingVersion: "openai-public-base-2026-08-25",
  modelName: "openai/gpt-5.4-nano",
  classifierContractVersion: "hold-cleaning-email-classifier-v2",
  extractorContractVersion: "hold-cleaning-email-extractor-v3",
  latestRunId: successRun.runId,
  latestRunAt: now,
  latestBackfillRunId: failedRun.runId,
  latestBackfillAt: now,
  latestProcessedAt: now,
  coverage: {
    retainedVoyageCount: 80,
    emailLinkedVoyageCount: 52,
    sources: {
      anyLinked: { coveredCount: 60, totalCount: 80, percentage: 75 },
      anyProcessed: { coveredCount: 48, totalCount: 80, percentage: 60 },
      cleaningForms: { coveredCount: 35, totalCount: 80, percentage: 43.8 },
      inventoryForms: { coveredCount: 27, totalCount: 80, percentage: 33.8 },
      emailMessages: { coveredCount: 52, totalCount: 80, percentage: 65 },
      extractedEmails: { coveredCount: 44, totalCount: 80, percentage: 55 },
      manualEntries: { coveredCount: 8, totalCount: 80, percentage: 10 },
      images: { coveredCount: 31, totalCount: 80, percentage: 38.8 },
    },
    processing: {
      outlookResolvable: { coveredCount: 179, totalCount: 184, percentage: 97.3 },
      classification: { coveredCount: 159, totalCount: 184, percentage: 86.4 },
      usableOutcome: { coveredCount: 154, totalCount: 184, percentage: 83.7 },
      relevantExtraction: { coveredCount: 86, totalCount: 91, percentage: 94.5 },
    },
    voyages: {
      intelligence: { coveredCount: 44, totalCount: 52, percentage: 84.6 },
      images: { coveredCount: 31, totalCount: 80, percentage: 38.8 },
    },
    fields: [
      { key: "CLEANING_PLAN", coveredCount: 36, totalCount: 52, percentage: 69.2 },
      { key: "CLEANING_STEPS", coveredCount: 32, totalCount: 52, percentage: 61.5 },
      { key: "HOLD_PROGRESS", coveredCount: 28, totalCount: 52, percentage: 53.8 },
      { key: "WORK_LOG", coveredCount: 40, totalCount: 52, percentage: 76.9 },
      { key: "OUTSTANDING_ITEMS", coveredCount: 18, totalCount: 52, percentage: 34.6 },
      { key: "INVENTORY", coveredCount: 29, totalCount: 52, percentage: 55.8 },
      { key: "FAILURES_OR_WARNINGS", coveredCount: 21, totalCount: 52, percentage: 40.4 },
      { key: "MAINTENANCE", coveredCount: 12, totalCount: 52, percentage: 23.1 },
      { key: "CLEANING_STANDARD", coveredCount: 35, totalCount: 52, percentage: 67.3 },
    ],
  },
  failureBreakdown: [
    {
      stage: "CLASSIFICATION",
      status: "FAILED_RETRYABLE",
      errorCode: "TRANSIENT_NETWORK",
      count: 5,
      firstOccurredAt: "2026-08-14T08:20:00.000Z",
      latestOccurredAt: now,
    },
  ],
});

export const mockRequestHoldCleaningEmailIntelligenceBackfill = (): HoldCleaningRefreshRequest =>
  mockRequestHoldCleaningRefresh();

export const mockRetryHoldCleaningEmailIntelligenceFailures = (): HoldCleaningRefreshRequest =>
  mockRequestHoldCleaningRefresh();
